import { useState } from 'react';

import { SegmentedControl } from 'cpool-components/input/SegmentedControl';
import { useTranslation } from 'react-i18next';
import { LuSearch, LuX } from 'react-icons/lu';

import { TemplatePickerListItem } from './TemplatePickerListItem';
import {
    TEMPLATE_PICKER_OVERLAY_HEIGHT_CLASS,
    WHATSAPP_TEMPLATE_PICKER_TABS,
    type WhatsAppTemplatePickerTab,
} from '~/features/whatsapp/constants/templatePicker';
import type { WhatsAppTemplate } from '~/features/whatsapp/hooks/useWhatsAppTemplates';
import { filterTemplatesForPickerTab } from '~/features/whatsapp/utils/filterTemplatesForPickerTab';
import type { TemplatePreviewSourceValues } from '~/features/whatsapp/utils/substituteTemplatePlaceholders';
import { cn } from '~/utils/cnTailwind';

type Props = {
    templates: ReadonlyArray<WhatsAppTemplate>;
    previewValues: TemplatePreviewSourceValues;
    hasLoanRequest: boolean;
    hasPartner: boolean;
    onUseTemplate: (template: WhatsAppTemplate) => void;
    onClose: () => void;
    // The template fetch failed — distinct from "catalog is genuinely empty" (EMPTY_SEARCH).
    hasLoadError?: boolean;
    onRetry?: () => void;
    defaultTab: WhatsAppTemplatePickerTab;
};

export const TemplatePickerOverlay = ({
    defaultTab,
    hasLoadError,
    hasLoanRequest,
    hasPartner,
    onClose,
    onRetry,
    onUseTemplate,
    previewValues,
    templates,
}: Props) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<WhatsAppTemplatePickerTab>(defaultTab);
    const [search, setSearch] = useState('');
    const [expandedTemplateName, setExpandedTemplateName] = useState<string | null>(null);

    const handleTabChange = (tab: WhatsAppTemplatePickerTab) => {
        setActiveTab(tab);
        setExpandedTemplateName(null);
    };

    const pickerTemplates = filterTemplatesForPickerTab(templates, activeTab);

    const normalizedSearch = search.trim().toLowerCase();
    const filteredTemplates = normalizedSearch
        ? pickerTemplates.filter((template) => {
              const language = template.languages[0];
              const body = language ? template.body[language] ?? '' : '';
              return (
                  template.displayName.toLowerCase().includes(normalizedSearch) ||
                  template.templateName.toLowerCase().includes(normalizedSearch) ||
                  body.toLowerCase().includes(normalizedSearch)
              );
          })
        : pickerTemplates;

    const groupedByFolder = [
        ...filteredTemplates
            .reduce((groups, template) => {
                groups.set(template.folder, [...(groups.get(template.folder) ?? []), template]);
                return groups;
            }, new Map<string, Array<WhatsAppTemplate>>())
            .entries(),
    ];

    return (
        <div
            className={cn(
                'flex flex-col rounded-md border border-outline-light bg-white shadow-lg',
                TEMPLATE_PICKER_OVERLAY_HEIGHT_CLASS,
            )}
        >
            <div className="flex items-center justify-between border-b border-outline-light px-3 py-2">
                <span className="text-sm font-semibold text-midnight">{t('WHATSAPP_CHAT.TEMPLATE_MODAL.TITLE')}</span>
                <button
                    type="button"
                    aria-label={t('GENERAL_CLOSE_BUTTON')}
                    onClick={onClose}
                    className="text-grey hover:text-midnight"
                >
                    <LuX />
                </button>
            </div>
            <div className="border-b border-outline-light px-3 py-2">
                <SegmentedControl
                    id="template-picker-tab"
                    ariaLabel={t('WHATSAPP_CHAT.TEMPLATE_MODAL.TABS_ARIA_LABEL')}
                    value={activeTab}
                    onChange={handleTabChange}
                    options={[
                        {
                            label: t('WHATSAPP_CHAT.TEMPLATE_MODAL.TABS.TEMPLATES'),
                            value: WHATSAPP_TEMPLATE_PICKER_TABS.TEMPLATES,
                        },
                        {
                            label: t('WHATSAPP_CHAT.TEMPLATE_MODAL.TABS.QUICK_REPLIES'),
                            value: WHATSAPP_TEMPLATE_PICKER_TABS.QUICK_REPLIES,
                        },
                    ]}
                    className="w-full"
                    segmentClassName="flex-1 text-center"
                    wide
                />
            </div>
            <div className="border-b border-outline-light px-3 py-2">
                <div className="relative">
                    <LuSearch className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-grey" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('WHATSAPP_CHAT.TEMPLATE_MODAL.SEARCH_PLACEHOLDER')}
                        className="w-full rounded-md border border-outline-light py-1.5 pl-8 pr-2 text-sm"
                    />
                </div>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
                {hasLoadError && (
                    <div className="px-3 py-4 text-center">
                        <p className="text-sm text-alert-error">{t('WHATSAPP_CHAT.TEMPLATE_MODAL.LOAD_ERROR')}</p>
                        {onRetry && (
                            <button
                                type="button"
                                onClick={onRetry}
                                className="mt-2 text-sm font-semibold text-midnight underline"
                            >
                                {t('WHATSAPP_CHAT.TEMPLATE_MODAL.RETRY')}
                            </button>
                        )}
                    </div>
                )}
                {!hasLoadError && groupedByFolder.length === 0 && (
                    <p className="px-3 py-4 text-center text-sm text-grey">
                        {t('WHATSAPP_CHAT.TEMPLATE_MODAL.EMPTY_SEARCH')}
                    </p>
                )}
                {groupedByFolder.map(([folder, folderTemplates]) => (
                    <div key={folder} className="mb-2">
                        <div className="px-3 py-1 text-xs font-semibold uppercase text-grey">{folder}</div>
                        {folderTemplates.map((template) => (
                            <TemplatePickerListItem
                                key={template.templateName}
                                template={template}
                                expanded={expandedTemplateName === template.templateName}
                                onToggle={() =>
                                    setExpandedTemplateName((prev) =>
                                        prev === template.templateName ? null : template.templateName,
                                    )
                                }
                                onUseTemplate={() => onUseTemplate(template)}
                                previewValues={previewValues}
                                hasLoanRequest={hasLoanRequest}
                                hasPartner={hasPartner}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};
